import clsx from "clsx";
import React from "react";

export interface RadioGroupOption {
  label: string | React.ReactNode;
  value: string;
  disabled?: boolean;
  extraContent?: React.ReactNode;
}

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const RadioGroup = ({
  name,
  options,
  direction = "vertical",
  selectedValue,
  defaultCheckedValue,
  onlyShowExtraContentForSelectedOption = true,
  disabled,
  onChange,
}: {
  name: string;
  options: RadioGroupOption[];
  direction?: "vertical" | "horizontal";
  selectedValue?: string;
  defaultCheckedValue?: string;
  onlyShowExtraContentForSelectedOption?: boolean; // if true, then the only extra content (`RadioGroupOption.extraContent`) will be that of the selected option; if false, then all extra content from all options will be shown (including disabled options)
  disabled?: boolean; // if true, then all options will be disabled
  onChange: (value: string) => void;
}) => {
  return (
    <fieldset
      className={classNames("flex", direction === "horizontal" ? "items-center gap-4" : "")}
      style={
        // Don't use `flex-col` above since it has a conflict with legacy flex.scss
        direction == "vertical" ? { flexDirection: "column" } : undefined
      }
      disabled={disabled}
    >
      {options.map((option, idx) => {
        let isSelected = option.value === selectedValue;
        let isLabelReactNode = React.isValidElement(option.label);
        let showExtraContent =
          option.extraContent && (onlyShowExtraContentForSelectedOption ? isSelected : true);

        return (
          <>
            <div
              key={option.value}
              className={clsx(
                "flex items-center",
                idx > 0 && direction == "vertical" && "mt-[12px]",
                showExtraContent && "mb-[12px]",
              )}
            >
              <input
                defaultChecked={option.value === defaultCheckedValue}
                name={name}
                id={`radio-option-${option.value}`}
                type="radio"
                checked={isSelected && !disabled}
                onChange={() => onChange(option.value)}
                value={option.value}
                className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-primary-600 checked:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
              />
              {isLabelReactNode ? (
                option.label
              ) : (
                <label
                  htmlFor={`radio-option-${option.value}`}
                  className={classNames(
                    "font-medium text-[14px] text-gray-700",
                    direction === "vertical" ? "ml-3" : "",
                    direction === "horizontal" ? "ml-1" : "",
                  )}
                >
                  {option.label}
                </label>
              )}
            </div>

            {showExtraContent && option.extraContent}
          </>
        );
      })}
    </fieldset>
  );
};

export default RadioGroup;
